import { flatten } from 'lodash';

import { AvailableLineDataRefNode } from '@futrli-sdk/consolidations';

import { forceLocalDate } from '../../../../../utils/dates';
import { LAST_DAY_OF_MONTH, LAST_DAY_OF_MONTH_NUM } from '../../../../../core/constants';
import { PROFIT_LOSS_ID, BALANCE_SHEET_ID } from '../../../components/formula/const';
import { RepeatingPattern } from '../../../types';
import { EntityAvailableLineDataRefNodeWithDepth } from './types';
import { deLocalizeFormula, validateLocalizedFormula } from '../../../../../utils/formulas/i18n-utils';

interface OutputOccursOnData {
  output_occurs_on_day?: number;
  output_occurs_on_month?: number;
}

/**
 * Recursive function for getting mentions data in flatten array so it can be display in mentions dropdown
 * @param ar initial array of nested data
 * @param depth children depth, we need depth so we can create nested structure in mentions dropdown
 */
export const getFormulaMentionsData = (
  ar: AvailableLineDataRefNode[],
  depth = 0
): EntityAvailableLineDataRefNodeWithDepth[] => {
  const newArr = [];
  for (const a of ar) {
    if (a.children.length > 0) {
      const newData = getFormulaMentionsData(a.children, depth + 1);
      newArr.push([
        {
          ref: a.ref,
          name: a.name,
          children: a.children,
          depth,
          disabled: a.disabled,
          classifier: a.classifier,
          organisation_id: a.organisation_id
        },
        ...newData
      ]);
    } else {
      newArr.push({
        ref: a.ref,
        name: a.name,
        children: [],
        depth,
        disabled: a.disabled,
        classifier: a.classifier,
        organisation_id: a.organisation_id
      });
    }
  }
  return flatten(newArr) as EntityAvailableLineDataRefNodeWithDepth[];
};

/**
 * Receives date and returns true if date is in past regards to current time
 * @param dateToCompare date to check if it is in past
 * @param isLastDayOfMonth comparing with is last day of month options
 */
export const checkIfIsInPast = (dateToCompare: Date, isLastDayOfMonth = false, forecastFromDateStr?: string | null) => {
  const forecastFromDate = forceLocalDate(forecastFromDateStr || '');

  if (isLastDayOfMonth) {
    if (
      dateToCompare.getFullYear() === forecastFromDate.getFullYear() &&
      dateToCompare.getMonth() === forecastFromDate.getMonth()
    ) {
      return false;
    }
  }

  if (
    dateToCompare.getDate() === forecastFromDate.getDate() &&
    dateToCompare.getFullYear() === forecastFromDate.getFullYear() &&
    dateToCompare.getMonth() === forecastFromDate.getMonth()
  ) {
    return false;
  }

  return dateToCompare.getTime() < forecastFromDate.getTime();
};

export const getLastDayOfMonth = (month: number, outputFrequency: RepeatingPattern) => {
  if (outputFrequency === RepeatingPattern.monthly) {
    return LAST_DAY_OF_MONTH_NUM;
  }
  const currentYear = new Date().getFullYear();
  const lastDayOfMonth = new Date(currentYear, month, 0);
  return lastDayOfMonth.getDate();
};

export const getFormulaOutputFrequency = (outputFrequency: RepeatingPattern) => {
  switch (outputFrequency) {
    case RepeatingPattern.monthly: {
      return 'MONTHLY';
    }
    case RepeatingPattern.daily: {
      return 'DAILY';
    }
    default: {
      return 'ANNUALLY';
    }
  }
};

export const checkFormulaValidation = (content: string, outputFrequency: RepeatingPattern) => {
  // Just to check if formula contains any words that are not in current language, for example
  // formula contains SUM and user language is French
  const isValidLocalization = validateLocalizedFormula(content);
  if (!isValidLocalization) {
    return false;
  }
  const formulaOutputFrequency = getFormulaOutputFrequency(outputFrequency);
  const { IsValid } = window.validateFormula
    ? window.validateFormula(deLocalizeFormula(content), formulaOutputFrequency)
    : { IsValid: true };
  return IsValid;
};

/**
 * Getting data from mentions menu (showed after pressing @ inside formula editor)
 * @param profitAndLoss
 * @param balanceSheet
 * @returns
 */
export const getAvailableLineData = (
  profitAndLoss: AvailableLineDataRefNode[],
  profitAndLossName: string,
  balanceSheet: AvailableLineDataRefNode[],
  balanceSheetName: string,
  showOnlyProfitAndLoss = false
) => {
  const organisationId = profitAndLoss[0].organisation_id;
  const updatedAvailableLineData = [
    {
      disabled: false,
      ref: PROFIT_LOSS_ID,
      name: profitAndLossName,
      children: [...profitAndLoss],
      classifier: 'static' as const,
      organisation_id: organisationId
    },
    {
      disabled: false,
      ref: BALANCE_SHEET_ID,
      name: balanceSheetName,
      children: [...balanceSheet],
      classifier: 'static' as const,
      organisation_id: organisationId
    }
  ];

  if (showOnlyProfitAndLoss) {
    updatedAvailableLineData.pop();
  }

  const initialOptions = getFormulaMentionsData(updatedAvailableLineData as AvailableLineDataRefNode[]);

  const parsedInitialOptions = initialOptions.map(o => ({
    id: o.ref,
    name: o.name,
    depth: o.depth,
    disabled: o.disabled
  }));

  return parsedInitialOptions;
};

export const getOutputOccursOnData = (
  outputFrequency: RepeatingPattern,
  frequencyOnDay: string,
  frequencyOnMonth: string
) => {
  const output: OutputOccursOnData = {
    output_occurs_on_day: frequencyOnDay === LAST_DAY_OF_MONTH ? LAST_DAY_OF_MONTH_NUM : Number(frequencyOnDay),
    output_occurs_on_month: Number(frequencyOnMonth)
  };

  switch (outputFrequency) {
    case RepeatingPattern.daily: {
      delete output.output_occurs_on_day;
      delete output.output_occurs_on_month;
      break;
    }
    case RepeatingPattern.weekly:
    case RepeatingPattern.monthly: {
      delete output.output_occurs_on_month;
      break;
    }
    default: {
      // annually or quarterly - leave data in-tact
    }
  }

  return output;
};
